// One host for every pipeline dialog and step. Mounted once (in
// RentalDashboard); the list rows, the quick-view panel and the detail page
// all open the SAME instances through useOrderActions().
//
//   openAction(order, 'reserve' | 'checkout' | 'return' | 'cancel')
//   openManual(order)      ⋯ → Change status manually…
//   openLateFee(order)     ⋯ → Add late fee manually
//   openCollect(order)     existing Collect Payment modal (owned by RentalDashboard)
//   openSign(order)        the existing /sign/:token flow (popup on desktop, new tab on touch)
//   version                bumps after every change so detail views re-fetch
//   activePage             { kind: 'checkout' | 'return', order } while the Check Out or Return PAGE is
//                          open — the host keeps the state; RentalDashboard renders <ActivePage /> in its
//                          main area (design: docs/DESIGN_CHECKIN_CHECKOUT_PAGES.md §2). Reserve, Cancel,
//                          manual override, late fee, Collect, Refund, Ledger stay overlays.
import React, { createContext, forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react';
import axios from '../../lib/axiosClient';
import ReserveConfirm from './ReserveConfirm';
import CheckOutPage from './CheckOutPage';
import ReturnPage from './ReturnPage';
import CancelConfirm from './CancelConfirm';
import ManualStatusDialog from './ManualStatusDialog';
import LateFeeDialog from './LateFeeDialog';
import CollectPaymentModal from './CollectPaymentModal';
import RefundDialog from './RefundDialog';
import LedgerSheet from './LedgerSheet';
import { isTouch, nextGoingOut } from './pipeline';

const Ctx = createContext(null);
export const useOrderActions = () => useContext(Ctx);

export function openSignWindow(order) {
  const url = `/sign/${order.public_token}`;
  // Second-monitor popup on a desk; a plain new tab on a phone/tablet (the
  // signature page posts ORDER_SIGNED back and closes itself either way).
  return isTouch() ? window.open(url, '_blank') : window.open(url, '_blank', 'width=900,height=800,left=1920,top=0');
}

// forwardRef: the component that MOUNTS the provider (RentalDashboard) cannot
// use the context itself, so it reaches the same host through this ref.
// onActivePage({ kind, orderId } | null): the shell learns a page opened/closed (hides its views, popups, phone sidebar).
// onPageExit(order, { finished }): where to land when a page closes — the shell shows that order's detail.
// nextUp: the dashboard's goingOut rows (reloaded by the shell after every change) for the "Next: N going out today →" affordance.
export const OrderActionProvider = forwardRef(function OrderActionProvider({ children, onChanged, showFlash, inventoryItems = [], dbCategories = [], onActivePage, onPageExit, nextUp = [] }, ref) {
  const [collectFor, setCollectFor] = useState(null);   // order the Collect Payment modal is open for
  const [refundFor, setRefundFor] = useState(null);
  const [ledgerFor, setLedgerFor] = useState(null);
  const [pending, setPending] = useState(null);   // { kind: 'action'|'manual'|'latefee', action?, order }
  const [version, setVersion] = useState(0);
  const pendingRef = useRef(pending); pendingRef.current = pending;
  const changed = useCallback((msg) => {
    setVersion(v => v + 1);
    if (msg && showFlash) showFlash(msg);
    if (onChanged) onChanged();
    const p = pendingRef.current;
    if (p) fresh(p.order).then(o => setPending(cur => (cur && cur.order?.id === o.id ? { ...cur, order: o } : cur)));
  }, [onChanged, showFlash]); // eslint-disable-line react-hooks/exhaustive-deps

  // Always act on a fresh copy (next_action, events, signed_at can be stale on a list row).
  const fresh = useCallback(async (order) => {
    try { const r = await axios.get(`/api/rentals/orders/${order.id}`); return r.data.order || order; }
    catch { return order; }
  }, []);

  const openRefund  = useCallback(async (order) => setRefundFor(await fresh(order)), [fresh]);
  const openLedger  = useCallback(async (order) => setLedgerFor(await fresh(order)), [fresh]);
  const openAction  = useCallback(async (order, action) => {
    // Payment-driven primary actions on a returned order are modals, not transitions.
    if (action === 'collect') { const o = await fresh(order); return setCollectFor({ order: o, defaultAmount: o.next_action?.dialog?.amount ?? null }); }
    if (action === 'refund')  { return setRefundFor(await fresh(order)); }
    setPending({ kind: 'action', action, order: await fresh(order) });
  }, [fresh]);
  const openManual  = useCallback(async (order) => { setPending({ kind: 'manual', order: await fresh(order) }); }, [fresh]);
  const openLateFee = useCallback(async (order) => { setPending({ kind: 'latefee', order: await fresh(order) }); }, [fresh]);
  const openSign    = useCallback((order) => openSignWindow(order), []);
  const openCollect = useCallback((order, opts = {}) => setCollectFor({ order, defaultAmount: opts.defaultAmount ?? null }), []);
  // POST /orders/:id/collect — on success the modal closes and everything underneath refreshes.
  const collect = useCallback(async (order, amt, method, note, sendReceipt, allowOverpay) => {
    try {
      await axios.post(`/api/rentals/orders/${order.id}/collect`, { amount: amt, method, note, direction: 'payment', send_receipt: sendReceipt, allow_overpay: !!allowOverpay });
      // The modal shows a brief "✓ Recorded …" state and closes itself (counter polish); the page beneath refreshes now.
      changed(`$${Number(amt).toFixed(2)} collected`);
      return true;
    } catch (e) { showFlash?.(e.response?.data?.error || 'Failed', 'error'); return false; }
  }, [changed, showFlash]);
  const close = useCallback(() => setPending(null), []);

  // POST /orders/:id/transition — the server re-runs every guard.
  const transition = useCallback(async (order, body) => {
    try {
      const r = await axios.post(`/api/rentals/orders/${order.id}/transition`, body);
      return { ok: true, order: r.data.order, event: r.data.event, close_out: r.data.close_out || null };
    } catch (e) {
      const d = e.response?.data || {};
      return { ok: false, status: e.response?.status, reason: d.reason || d.error || 'Request failed', body: d };
    }
  }, []);

  // A signed contract in the popup refreshes whatever step is open.
  useEffect(() => {
    const on = async (ev) => {
      if (ev.data?.type !== 'ORDER_SIGNED') return;
      const p = pendingRef.current;
      if (p?.order?.id === ev.data.orderId) setPending({ ...p, order: await fresh(p.order) });
      changed();
    };
    window.addEventListener('message', on);
    return () => window.removeEventListener('message', on);
  }, [fresh, changed]);

  // Guarded like `changed`: a refresh started before a page/dialog closed must not resurrect it after.
  const refreshPending = useCallback(async () => {
    const p = pendingRef.current; if (!p) return;
    const o = await fresh(p.order);
    setPending(cur => (cur && cur.order?.id === o.id ? { ...cur, order: o } : cur));
  }, [fresh]);

  // The Check Out and Return PAGES. Same pending state as the dialogs; rendered by the shell, not here.
  const activePage = pending?.kind === 'action' && (pending.action === 'checkout' || pending.action === 'return') ? { kind: pending.action, order: pending.order } : null;
  const activeKind = activePage?.kind || null, activeId = activePage?.order?.id ?? null;
  useEffect(() => { onActivePage && onActivePage(activeKind ? { kind: activeKind, orderId: activeId } : null); }, [activeKind, activeId]); // eslint-disable-line react-hooks/exhaustive-deps
  const exitPage = useCallback((order, finished) => { setPending(null); onPageExit && onPageExit(order, { finished }); }, [onPageExit]);
  const pageProps = activePage ? {
    order: activePage.order,
    onBack: () => exitPage(activePage.order, false),   // nothing was written: local ticks / typed fields are discarded
    transition, onRefresh: refreshPending, openSign, openCollect, openRefund, inventoryItems, dbCategories,
    onDone: (o) => { changed(`Order ${o.order_number || '#' + o.id} ${activePage.kind === 'return' ? 'returned' : 'is out'}`); exitPage(o, true); },
  } : null;

  const value = { openAction, openManual, openLateFee, openCollect, openRefund, openLedger, openSign, transition, version, refresh: changed, showFlash, activePage, pageProps, nextUp };
  useImperativeHandle(ref, () => ({ openAction, openManual, openLateFee, openCollect, openRefund, openLedger, refresh: changed }), [openAction, openManual, openLateFee, openCollect, openRefund, openLedger, changed]);

  return (
    <Ctx.Provider value={value}>
      {children}
      {pending?.kind === 'action' && pending.action === 'reserve'  && <ReserveConfirm order={pending.order} onClose={close} transition={transition} onRefresh={refreshPending} openSign={openSign} openCollect={openCollect} onDone={(o) => { close(); changed(`Order ${o.order_number || '#' + o.id} reserved`); }} />}
      {/* checkout and return: PAGES — see <ActivePage />, rendered by the shell in its main area */}
      {pending?.kind === 'action' && pending.action === 'cancel'   && <CancelConfirm order={pending.order} onClose={close} transition={transition} onDone={(o) => { close(); changed(`Order ${o.order_number || '#' + o.id} cancelled`); }} />}
      {pending?.kind === 'manual'  && <ManualStatusDialog order={pending.order} onClose={close} onDone={() => { close(); changed('Status changed'); }} showFlash={showFlash} />}
      {pending?.kind === 'latefee' && <LateFeeDialog order={pending.order} onClose={close} onDone={() => { close(); changed('Late fee added'); }} showFlash={showFlash} />}
      {ledgerFor && <LedgerSheet order={ledgerFor} onClose={() => setLedgerFor(null)} />}
      {refundFor && <RefundDialog order={refundFor} onClose={() => setRefundFor(null)} onDone={() => { setRefundFor(null); changed('Refund recorded'); }} showFlash={showFlash} />}
      {/* last on purpose: paints above any open step */}
      {collectFor && <CollectPaymentModal order={collectFor.order} defaultAmount={collectFor.defaultAmount} onClose={() => setCollectFor(null)} onCollect={(amt, method, note, receipt, allowOverpay) => collect(collectFor.order, amt, method, note, receipt, allowOverpay)} />}
    </Ctx.Provider>
  );
});

// "Next: N going out today →" — on the Check Out page header and the order detail header
// (design §4). Reads the shell's dashboard rows through the context; N excludes the order
// on screen; hidden at 0. Tap opens the Check Out page for the earliest one (fresh fetch,
// guards re-run there as from anywhere else). `narrow`: a full-width bar under the header.
export function NextUp({ excludeId, narrow }) {
  const ctx = useContext(Ctx);
  const { count, next } = nextGoingOut(ctx?.nextUp, excludeId);
  if (!ctx || count === 0 || !next) return null;
  const label = `Next: ${count} going out today →`;
  return (
    <button type="button" data-testid="next-up" data-count={count} data-next-id={next.id} title={`${next.order_number || '#' + next.id} · ${next.client_name || ''}`}
      onClick={() => ctx.openAction(next, 'checkout')}
      style={{ background: '#1a2a4a', border: '1px solid #1e3a6a', color: '#60a5fa', borderRadius: narrow ? 8 : 20, padding: narrow ? '10px 12px' : '5px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', width: narrow ? '100%' : 'auto', textAlign: narrow ? 'center' : 'left', boxSizing: 'border-box' }}>
      {label}
    </button>
  );
}

// Rendered by the shell (RentalDashboard) inside its main area, in place of
// the current view, while a page is open. Reads the host's state; owns nothing.
export function ActivePage() {
  const ctx = useContext(Ctx);
  if (!ctx?.activePage || !ctx.pageProps) return null;
  if (ctx.activePage.kind === 'checkout') return <CheckOutPage {...ctx.pageProps} />;
  if (ctx.activePage.kind === 'return')   return <ReturnPage {...ctx.pageProps} />;
  return null;
}
